// CETRA-38: keep legacy effect timing while clearing release ESLint warnings.
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useEffect, useRef, useState } from 'react';
import { Box, Typography, CircularProgress, useMediaQuery, useTheme } from '@mui/material';
import StoreContext from '../../../react-core-lib/components/app/store-context';
import { CenteredWrapper } from '../../UIControls/centered-wrapper';
import DownloadUtils from '../../../utils/download-utils';
import UserApi from '../../../rest-api/user-api';
import { useUnsavedChanges } from '../UnsavedChangesContext';
import SettingsUser from './settings-user';
import SettingsClinic from './settings-clinic';
import UnsavedChangesModal from '../../UIControls/UnsavedChangesModal';
import SettingsPassword from './settings-password';
import SettingsDeleteAccount from './settings-delete-account';
import SettingsBilling from './settings-billing';
import SettingsSubscription from './settings-subscription';

export function SettingsMultiUserScreen() {
    const appStateStore = useContext(StoreContext);
    const { authStore, userDataStore } = useContext(StoreContext);
    const [clinicianData, setClinicianData] = useState(null);
    const [clinicImage, setClinicImage] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isChangePasswordDialogOpen, setIsChangePasswordDialogOpen] = useState(false);
    const [trialExpiryTime, setTrialExpiryTime] = useState(null);

    const { hasUnsavedChanges, setHasUnsavedChanges, onConfirmNavigation, onCancelNavigation, showPrompt } =
        useUnsavedChanges();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const rightSectionsScrollRef = useRef(null);
    const sectionRefs = useRef({});
    const [activeSection, setActiveSection] = useState('Practice details');
    const settingsSectionTitles = ['Practice details', 'Personal details', 'Password', 'Billing', 'Subscription'];

    useEffect(() => {
        const handleBeforeUnload = (e) => {
            if (hasUnsavedChanges) {
                e.preventDefault();
                e.returnValue = '';
            }
        };

        if (hasUnsavedChanges) {
            window.addEventListener('beforeunload', handleBeforeUnload);
        }

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [hasUnsavedChanges]);

    useEffect(() => {
        fetchClinicianData();
    }, []);

    useEffect(() => {
        if (loading || isMobile) return;
        const container = rightSectionsScrollRef.current;
        if (!container) return;

        const onScroll = () => {
            const visibleSectionOrder = ['Practice details', 'Personal details', 'Password', 'Billing', 'Subscription'];
            let current = visibleSectionOrder[0];

            const containerRect = container.getBoundingClientRect();
            const activationLine = Math.min(container.clientHeight * 0.35, 180);
            visibleSectionOrder.forEach((title) => {
                const node = sectionRefs.current[title];
                if (!node) return;
                const sectionTopRelativeToContainer = node.getBoundingClientRect().top - containerRect.top;
                if (sectionTopRelativeToContainer <= activationLine) {
                    current = title;
                }
            });
            setActiveSection(current);
        };

        onScroll();
        container.addEventListener('scroll', onScroll, { passive: true });
        window.addEventListener('scroll', onScroll, { passive: true });

        const resizeObserver = new ResizeObserver(onScroll);
        resizeObserver.observe(container);

        Object.values(sectionRefs.current).forEach((node) => {
            if (node) resizeObserver.observe(node);
        });

        return () => {
            container.removeEventListener('scroll', onScroll);
            window.removeEventListener('scroll', onScroll);
            resizeObserver.disconnect();
        };
    }, [loading, isMobile, clinicianData?.subscriptionData?.stripeCustomerId]);

    const fetchClinicianData = async () => {
        setLoading(true);
        try {
            const clinicianDataResponse = await UserApi.getClinicianData(authStore.idToken);
            if (clinicianDataResponse) {
                // let currentUserIntegrations = null;
                if (clinicianDataResponse.multiuserData && clinicianDataResponse.multiuserData.multiuserUsers) {
                    const foundUser = clinicianDataResponse.multiuserData.multiuserUsers.find(
                        (user) => user.userId === userDataStore?.userId,
                    );
                    if (foundUser) {
                        clinicianDataResponse.userData.firstName = foundUser.firstName;
                        clinicianDataResponse.userData.lastName = foundUser.lastName;
                        clinicianDataResponse.userData.emailAddress = foundUser.emailAddress;
                        clinicianDataResponse.userData.jobTitle = foundUser.jobTitle;
                        clinicianDataResponse.userData.mobile = foundUser.mobile;
                        clinicianDataResponse.userData.yearsExperience = foundUser.yearsExperience;
                        // currentUserIntegrations = foundUser.integrations || null;
                    }
                }

                clinicianDataResponse.clinicData = clinicianDataResponse.clinicData || {};
                //  var formattedData = {
                //     firstName: clinicianDataResponse.userData.firstName,
                //     lastName: clinicianDataResponse.userData.lastName,
                //     emailAddress: clinicianDataResponse.userData.emailAddress,
                //     mobile: clinicianDataResponse.userData.mobile || '',
                //     jobTitle: clinicianDataResponse.userData.jobTitle || '',
                //     yearsExperience: clinicianDataResponse.userData.yearsExperience || '',
                //     clinicName: clinicData.clinicName || '',
                //     clinicNumber: clinicData.clinicNumber || '',
                //     clinicSize: clinicData.clinicSize || '',
                //     clinicType: clinicData.clinicType || '',
                //     clinicImageId: '',
                //     showLogoInHandouts: clinicianDataResponse.showLogoInHandouts,
                //     handouts: JSON.stringify(clinicianDataResponse.handouts),
                //     subscriptionData: clinicianDataResponse.subscriptionData || {},
                //     integrations: currentUserIntegrations,
                // };
                setClinicianData(clinicianDataResponse);

                const { subscriptionEndDate } = clinicianDataResponse.subscriptionData || {};
                if (subscriptionEndDate) {
                    const differenceInMilliseconds = subscriptionEndDate - Date.now();
                    const differenceInDays = differenceInMilliseconds / (24 * 60 * 60 * 1000);
                    setTrialExpiryTime(Math.floor(differenceInDays));
                }

                const clinicData = clinicianDataResponse.clinicData || {};
                if (clinicData.clinicImageId) {
                    const payload = {
                        files: [{ method: 'get', key: clinicData.clinicImageId }],
                        contentCategory: 'clinic_image',
                    };
                    const clinicImageURL = await UserApi.generatePresignedUrls(authStore.idToken, payload);
                    if (clinicImageURL?.getUrls?.[0]?.presignedUrl) {
                        setClinicImage(clinicImageURL.getUrls[0].presignedUrl);
                        await DownloadUtils.downloadImageFromPresignedUrl(clinicImageURL.getUrls[0].presignedUrl);
                    }
                } else {
                    setClinicImage('');
                }
            }
        } catch (error) {
            console.error('Failed to fetch clinician data:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSignout = async () => {
        await authStore.logout();
        await appStateStore.clearUserStores();
        localStorage.clear();
    };

    const handleChangeInput = (e) => {
        const { name, value } = e.target;
        const [parentKey, childKey] = name.split('.');

        setClinicianData((prevState) => ({
            ...prevState,
            [parentKey]: {
                ...prevState[parentKey],
                [childKey]: value,
            },
        }));
        setHasUnsavedChanges(true);
    };

    const updateClinicDataMultiuser = async () => {
        const clinicDataPayload = {
            firstName: clinicianData.userData.firstName,
            lastName: clinicianData.userData.lastName,
            emailAddress: clinicianData.userData.emailAddress,
            mobile: clinicianData.userData.mobile || '',
            jobTitle: clinicianData.userData.jobTitle || '',
            yearsExperience: clinicianData.userData.yearsExperience || '',
            inviteId: userDataStore.userData.inviteId,
            action: 'updateMultiuser',
        };

        try {
            await UserApi.updateClinicianDataMultiuser(authStore.idToken, clinicDataPayload);
            await fetchClinicianData();
            setHasUnsavedChanges(false);
        } catch (error) {
            console.error('Failed to update clinic data:', error);
        }
    };

    const scrollToSection = (sectionTitle) => {
        const container = rightSectionsScrollRef.current;
        const node = sectionRefs.current[sectionTitle];

        if (!container || !node) return;

        const containerRect = container.getBoundingClientRect();
        const nodeRect = node.getBoundingClientRect();
        const targetTop = container.scrollTop + (nodeRect.top - containerRect.top);

        container.scrollTo({
            top: Math.max(targetTop, 0),
            behavior: 'smooth',
        });
        setActiveSection(sectionTitle);
    };

    return (
        <CenteredWrapper
            component="main"
            sx={{ zIndex: 1, position: 'relative', overflowX: 'hidden', background: '#F0F0FC' }}
        >
            {loading ? (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                    <CircularProgress />
                </Box>
            ) : (
                <Box
                    sx={{ gap: 0, width: '100%', maxWidth: 1320, background: '#F0F0FC' }}
                    className="settings-container"
                >
                    <Box>
                        <Box style={{ textAlign: 'left', marginTop: '24px' }}>
                            <Typography
                                variant="h4"
                                sx={{
                                    color: '#000453',
                                    marginBottom: 3,
                                    fontSize: '40px',
                                    fontWeight: 600,
                                    lineHeight: '40px',
                                    letterSpacing: '-1.6px',
                                }}
                            >
                                Manage your account
                            </Typography>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                {trialExpiryTime <= 30 && clinicianData?.subscriptionData?.accountType !== 'Free' && (
                                    <Typography variant="subtitle" style={{ marginBottom: '0' }}>
                                        You have <strong> {trialExpiryTime} days</strong> left of your{' '}
                                        {clinicianData?.subscriptionData?.accountType}{' '}
                                        {clinicianData?.subscriptionData?.trialActive ? 'free trial' : ''} plan.
                                    </Typography>
                                )}
                            </Box>

                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: { xs: 'column', md: 'row' },
                                    gap: { xs: 2, md: 3 },
                                    alignItems: { xs: 'flex-start', md: 'stretch' },
                                }}
                            >
                                {!isMobile && (
                                    <Box
                                        sx={{
                                            width: 280,
                                            background: '#fff',
                                            borderRadius: '30px',
                                            border: '1px solid rgba(102, 102, 102, 0.15)',
                                            overflow: 'hidden',
                                            position: 'sticky',
                                            top: 120,
                                            alignSelf: 'flex-start',
                                            minHeight: 'calc(100vh - 140px)',
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                height: 18,
                                                background: '#fff',
                                                borderBottom: '1px solid rgba(229,229,229,1)',
                                            }}
                                        />
                                        {settingsSectionTitles.map((item) => (
                                            <Box
                                                key={item}
                                                onClick={() => scrollToSection(item)}
                                                sx={{
                                                    px: 3,
                                                    py: 0,
                                                    minHeight: 78,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    color: '#000453',
                                                    fontWeight: 400,
                                                    fontSize: '24px',
                                                    fontStyle: 'normal',
                                                    lineHeight: '24px',
                                                    letterSpacing: '-0.48px',
                                                    backgroundColor: activeSection === item ? '#F0F0FC' : '#fff',
                                                    borderLeft:
                                                        activeSection === item
                                                            ? '2px solid #FF669B'
                                                            : '2px solid transparent',
                                                    borderBottom: '1px solid rgba(229,229,229,1)',
                                                    cursor: sectionRefs.current[item] ? 'pointer' : 'default',
                                                }}
                                            >
                                                {item}
                                            </Box>
                                        ))}
                                    </Box>
                                )}

                                <Box
                                    ref={rightSectionsScrollRef}
                                    sx={{
                                        flex: 1,
                                        minWidth: 0,
                                        maxHeight: { xs: 'none', md: 'calc(100vh - 140px)' },
                                        overflowY: { xs: 'visible', md: 'auto' },
                                        pr: { md: 1 },
                                    }}
                                >
                                    <Box
                                        ref={(node) => {
                                            sectionRefs.current['Practice details'] = node;
                                        }}
                                    >
                                        <SettingsClinic
                                            setClinicianData={setClinicianData}
                                            clinicianData={clinicianData}
                                            clinicImage={clinicImage}
                                            handleChangeInput={handleChangeInput}
                                            handleSignout={handleSignout}
                                            setHasUnsavedChanges={setHasUnsavedChanges}
                                            showPrompt={showPrompt}
                                            onConfirmNavigation={onConfirmNavigation}
                                            onCancelNavigation={onCancelNavigation}
                                            hasUnsavedChanges={hasUnsavedChanges}
                                            loading={loading}
                                            setLoading={setLoading}
                                            readOnly={true}
                                            showEditAction={false}
                                            bannerText={`You're part of the ${clinicianData?.clinicData?.clinicName} workspace`}
                                            layout="two-column"
                                            sectionSx={{ mt: 0 }}
                                        />
                                    </Box>

                                    <Box
                                        ref={(node) => {
                                            sectionRefs.current['Personal details'] = node;
                                        }}
                                    >
                                        <SettingsUser
                                            clinicianData={clinicianData}
                                            handleChangeInput={handleChangeInput}
                                            isMultiUser={true}
                                            updateClinicDataMultiuser={updateClinicDataMultiuser}
                                            showEditAction={true}
                                            canEdit={true}
                                            layout="two-column"
                                        />
                                    </Box>

                                    <Box
                                        ref={(node) => {
                                            sectionRefs.current['Password'] = node;
                                        }}
                                    >
                                        <SettingsPassword
                                            isChangePasswordDialogOpen={isChangePasswordDialogOpen}
                                            setIsChangePasswordDialogOpen={setIsChangePasswordDialogOpen}
                                            showEditAction={true}
                                            canEdit={true}
                                        />
                                    </Box>

                                    {clinicianData?.subscriptionData?.stripeCustomerId && (
                                        <Box
                                            ref={(node) => {
                                                sectionRefs.current['Billing'] = node;
                                            }}
                                        >
                                            <SettingsBilling showEditAction={false} />
                                        </Box>
                                    )}

                                    <Box
                                        ref={(node) => {
                                            sectionRefs.current['Subscription'] = node;
                                        }}
                                    >
                                        <SettingsSubscription
                                            plan={clinicianData?.subscriptionData?.accountType}
                                            canManagePlan={false}
                                        />
                                    </Box>

                                    <SettingsDeleteAccount />
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                </Box>
            )}
            <UnsavedChangesModal
                open={showPrompt}
                onDiscardChanges={onConfirmNavigation}
                onCancel={onCancelNavigation}
            />
        </CenteredWrapper>
    );
}
